import React, { useEffect, useState } from 'react';
import { tv } from 'tailwind-variants';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Input from '../Input';
import Strength from './Strength';
import Hint from './Hint';
import HtmlInput from '../../interfaces/HtmlInput';
import validations from '../../helpers/Validations';
import useGTMEventsTracker from '../../hooks/useGTMEventsTracker';
import Typography from '../Typography';

const eyeButton = tv({
  base: 'icon-visible-password absolute cursor-pointer text-neutral-medium top-1/2 translate-y-[calc(-50%+3px)]',
  variants: {
    invalid: {
      // error icon: right-3 (12px) + 18px wide = occupies 12–30px from right
      // eye must clear it: right-10 (40px) gives 10px gap
      true: 'right-10',
      false: 'right-4',
    },
  },
  defaultVariants: { invalid: false },
});

interface AttributesGTM {
  event: string;
  eventCategory: string;
  eventAction: string;
  eventLabel: string;
}
interface PasswordProps {
  inputProps?: any;
  label?: string;
  showValidation?: boolean;
  onChange: Function;
  loginPassword?: boolean;
  required?: boolean;
  gtmAttributes?: AttributesGTM;
}

function Password({
  inputProps,
  label,
  showValidation = true,
  onChange,
  loginPassword,
  required = false,
  gtmAttributes,
}: PasswordProps) {
  const [password, setPassword] = useState('');
  const [visible, setVisible] = useState(false);
  const [invalid, setInvalid] = useState(false);

  const [hasUpperAndLower, setHasUpperAndLower] = useState(false);
  const [hasNumber, setHasNumber] = useState(false);
  const [hasSpecialChar, setHasSpecialChar] = useState(false);
  const [minAndMaxLength, setMinAndMaxLength] = useState(false);

  const matchPassword: Function = (regex: RegExp) => regex.test(password);

  const strongPassword: Boolean =
    hasNumber && hasSpecialChar && minAndMaxLength && hasUpperAndLower;
  const rules = required ? { rules: [(val: HtmlInput) => validations.required(val, 'senha')] } : '';

  useEffect(() => {
    onChange({
      isValid: hasNumber && hasSpecialChar && minAndMaxLength && hasUpperAndLower,
      value: password,
    });
  }, [hasUpperAndLower, hasNumber, hasSpecialChar, minAndMaxLength]);

  useEffect(() => {
    setHasNumber(matchPassword(/\d/));
    setHasSpecialChar(matchPassword(/([!@#$%^&*-+=[\]{}|:',?/`~"();]|_|-)/));
    setMinAndMaxLength(password.length >= 8 && password.length <= 16);
    setHasUpperAndLower(matchPassword(/[a-z]/) && matchPassword(/[A-Z]/));
  }, [password]);

  const gtmMEventsTracker = useGTMEventsTracker();

  const hidePassword = () => {
    setVisible(!visible);
    if (gtmAttributes) gtmAttributes.eventLabel = visible ? 'ocultou a senha' : 'exibiu a senha';
    gtmMEventsTracker(gtmAttributes);
  };

  return (
    <div id="mp__password" className="text-sm">
      <div className="relative">
        <Input
          id="mp__input-password"
          labelId="mp__label-senha"
          type={visible ? 'text' : 'password'}
          showErrorIcon={false}
          name="password"
          label={label ?? 'Senha'}
          onChange={(e: any) => {
            const target = e.target as HTMLInputElement;
            if (target) {
              setPassword(target.value);
              onChange({
                ...e,
                isValid: minAndMaxLength && hasUpperAndLower && hasSpecialChar && hasNumber,
              });
            } else if (typeof e.isValid !== undefined) {
              setInvalid(!e.isValid);
            } else {
              setInvalid(false);
            }
          }}
          loginPassword={loginPassword}
          {...inputProps}
          {...rules}
        >
          <button
            type="button"
            onClick={hidePassword}
            aria-label={visible ? 'Ocultar senha' : 'Exibir senha'}
            className={eyeButton({ invalid })}
          >
            {visible ? <Visibility /> : <VisibilityOff />}
          </button>
        </Input>
      </div>
      {showValidation && !loginPassword && (
        <>
          <Strength strong={strongPassword} />
          <div className="flex flex-col gap-3">
            <Typography
              variant="caption"
              id="mp__password-hint-title"
              className="text-neutral-dark"
            >
              Sua senha precisa ter:
            </Typography>
            <ul className="pl-0 flex flex-col gap-1.5">
              <Hint
                id="mp__password-rule-length"
                text="Entre 8 e 16 caracteres"
                valid={minAndMaxLength}
              />
              <Hint
                id="mp__password-rule-case"
                text="Letras minúsculas e maiúsculas"
                valid={hasUpperAndLower}
              />
              <Hint
                id="mp__password-rule-special-char"
                text={
                  'Um dos caracteres especiais ! @ # $ % ^ & * - _ + = [ ] { } | : \' , ? / ` ~ " ( ) ;'
                }
                valid={hasSpecialChar}
              />
              <Hint id="mp__password-rule-number" text="Pelo menos um número" valid={hasNumber} />
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

export default Password;
